'use client'

import { AlertIcon } from '@repo/ui/AlertIcon'
import { Avatar } from '@repo/ui/Avatar'
import { ReactNode, useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { CloseIcon } from '@repo/ui/CloseIcon'
import { DateSelection } from '@repo/ui/DateSelection'
import { EnterIcon } from '@repo/ui/EnterIcon'
import { FilterIcon } from '@repo/ui/FilterIcon'
import { GridRenderHeaderFilterProps } from '@mui/x-data-grid-pro'
import { Pill } from '@repo/ui/Pill'
import { useDataGridFilterStore } from '@repo/store/useDataGridFilterStore'
import { useFeeEarnerStore } from '@repo/store/useFeeEarnerStore'
import { useKeyDateStore } from '@repo/store/useKeyDateStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useTheme } from '@mui/material/styles'
import { v4 as uuidv4 } from 'uuid'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIFormControl from '@mui/material/FormControl'
import MUIInputLabel from '@mui/material/InputLabel'
import MUIMenuItem from '@mui/material/MenuItem'
import MUIPaper from '@mui/material/Paper'
import MUIPopper from '@mui/material/Popper'
import MUISelect from '@mui/material/Select'
import MUITextField from '@mui/material/TextField'
import MUITypography from '@mui/material/Typography'

/** Common menu item styles used across the filter components */
const menuItemStyles = {
  borderRadius: '4px',
  cursor: 'pointer',
  margin: '6px 6px',
  minHeight: '38px',
  minWidth: 'calc(264px - 38px)',
  pb: 0,
  pl: '8px',
  pt: 0,
  width: '100%'
}

/** A shared form control wrapper for consistent styling */
const FormControlDataGridWrapper = ({
  children,
  label,
  sx,
  shrink
}: {
  children: ReactNode
  label: string
  sx?: any
  shrink?: boolean
}) => (
  <MUIFormControl
    sx={{
      width: '100%',
      [`& > label`]: {
        backgroundColor: shrink ? '#f5f6f8' : 'transparent',
        top: shrink ? 0 : '-10px',
        transform: shrink ? 'translate(14px, -9px) scale(0.75)' : 'translate(8px, 15px) scale(1)'
      },
      [`& > label[data-shrink='true']`]: {
        backgroundColor: '#f5f6f8',
        top: 0,
        transform: 'translate(14px, -9px) scale(0.75)'
      },
      ...sx
    }}
  >
    <MUIInputLabel id={`${label}-filter-label`}>{label}</MUIInputLabel>
    {children}
  </MUIFormControl>
)

/**
 * A generic select filter component that encapsulates shared styling and behavior.
 * Accepts a label, value, onChange, a renderValue function, and its children (the options).
 */
type SelectDataGridFilterWrapperProps = {
  label: string
  value: any
  onChange: (e: any) => void
  renderValue?: (selected: any) => React.ReactNode
  children: React.ReactNode
  inputProps?: any
}

export const SelectDataGridFilterWrapper = ({
  label,
  value,
  onChange,
  renderValue,
  children,
  inputProps
}: SelectDataGridFilterWrapperProps) => {
  return (
    <FormControlDataGridWrapper label={label}>
      <MUISelect
        data-testid={`${label}-filter`}
        multiple
        value={value}
        onChange={onChange}
        renderValue={renderValue}
        placeholder="Select"
        sx={{
          height: '32px',
          mb: 0,
          width: '100%',
          [`& .MuiSelect-select`]: {
            height: '18px !important',
            pt: '6px',
            pb: '6px',
            pl: '8px'
          },
          [`& .MuiSelect-nativeInput`]: {
            height: '32px !important',
            pt: 0,
            pb: 0,
            pl: '8px'
          },
          [`& .MuiSvgIcon-root`]: {
            right: 0
          }
        }}
        {...inputProps}
      >
        {children}
      </MUISelect>
    </FormControlDataGridWrapper>
  )
}

/**
 * A helper for rendering the value of select filters.
 * Displays a single selection or a summary if multiple items are selected.
 */
export const defaultRenderValue = (selected: string[]): string => {
  if (selected.length === 1) return selected[0]
  if (selected.length > 1) return `${selected[0]} + ${selected.length - 1}`
  return 'Select'
}

/**
 * A clearable text field for filtering.
 * Updates the appropriate (clients, matters, keyDates, expenses, or overtime) filters in the store.
 */
export const TextFieldDataGridFilter = ({
  filterType,
  isClientsTable,
  isKeyDatesTable,
  isExpensesTable,
  isOvertimeTable,
  isThirdPartyFormsTable,
  isDocumentBundlesTable,
  isMatterSpecific,
  inputRef: externalInputRef,
  placeholder = 'Contains',
  ...otherProps
}: {
  filterType: string
  isClientsTable?: boolean
  isKeyDatesTable?: boolean
  isExpensesTable?: boolean
  isOvertimeTable?: boolean
  isThirdPartyFormsTable?: boolean
  isDocumentBundlesTable?: boolean
  isMatterSpecific?: boolean
  placeholder?: string
  inputRef?: React.RefObject<HTMLInputElement>
}) => {
  const {
    clientsTableFilters,
    mattersTableFilters,
    keyDatesTableFilters,
    matterSpecificKeyDatesTableFilters,
    expensesTableFilters,
    overtimeTableFilters,
    thirdPartyFormsTableFilters,
    documentBundlesTableFilters,
    setClientsTableFilters,
    setMattersTableFilters,
    setKeyDatesTableFilters,
    setExpensesTableFilters,
    setOvertimeTableFilters,
    setThirdPartyFormsTableFilters,
    setDocumentBundlesTableFilters,
    mattersTableFilterCount,
    clientsTableFilterCount,
    keyDatesTableFilterCount,
    matterSpecificKeyDatesTableFilterCount,
    expensesTableFilterCount,
    overtimeTableFilterCount,
    thirdPartyFormsTableFilterCount,
    documentBundlesTableFilterCount
  } = useDataGridFilterStore()

  useEffect(() => {
    const shouldResetDisplayedValue = isExpensesTable
      ? expensesTableFilterCount === 0
      : isOvertimeTable
        ? overtimeTableFilterCount === 0
        : isThirdPartyFormsTable
          ? thirdPartyFormsTableFilterCount === 0
          : isDocumentBundlesTable
            ? documentBundlesTableFilterCount === 0
            : isClientsTable
              ? clientsTableFilterCount === 0
              : isKeyDatesTable
                ? isMatterSpecific
                  ? matterSpecificKeyDatesTableFilterCount === 0
                  : keyDatesTableFilterCount === 0
                : mattersTableFilterCount === 0

    if (shouldResetDisplayedValue && combinedInputTextRef?.current) combinedInputTextRef.current.value = ''
  }, [
    mattersTableFilterCount,
    clientsTableFilterCount,
    keyDatesTableFilterCount,
    matterSpecificKeyDatesTableFilterCount,
    expensesTableFilterCount,
    overtimeTableFilterCount,
    thirdPartyFormsTableFilterCount,
    documentBundlesTableFilterCount,
    isClientsTable,
    isKeyDatesTable,
    isExpensesTable,
    isOvertimeTable,
    isThirdPartyFormsTable,
    isDocumentBundlesTable,
    isMatterSpecific
  ])

  // Choose the appropriate filters object from the store.
  const storeFilters = isExpensesTable
    ? (expensesTableFilters?.filters ?? {})
    : isOvertimeTable
      ? (overtimeTableFilters?.filters ?? {})
      : isThirdPartyFormsTable
        ? (thirdPartyFormsTableFilters?.filters ?? {})
        : isDocumentBundlesTable
          ? (documentBundlesTableFilters?.filters ?? {})
          : isClientsTable
            ? (clientsTableFilters.filters ?? {})
            : isKeyDatesTable
              ? ((isMatterSpecific ? matterSpecificKeyDatesTableFilters.filters : keyDatesTableFilters.filters) ?? {})
              : (mattersTableFilters.filters ?? {})

  // Derive the initial value from the store.
  let initialTextValue = storeFilters[filterType] ?? ''
  if (
    !isClientsTable &&
    !isKeyDatesTable &&
    !isExpensesTable &&
    !isOvertimeTable &&
    !isThirdPartyFormsTable &&
    !isDocumentBundlesTable &&
    filterType === 'clientNo' &&
    storeFilters.matterNo
  ) {
    initialTextValue = `${storeFilters.clientNo}-${storeFilters.matterNo}`
  }

  useEffect(() => {
    const valFromStore = storeFilters?.[filterType]
    let display = valFromStore ?? ''
    // (only matters table shows clientNo-matterNo as a single field)
    if (
      !isClientsTable &&
      !isKeyDatesTable &&
      !isExpensesTable &&
      !isOvertimeTable &&
      !isThirdPartyFormsTable &&
      !isDocumentBundlesTable &&
      filterType === 'clientNo' &&
      storeFilters?.matterNo
    ) {
      const cn = storeFilters?.clientNo ?? ''
      const mn = storeFilters?.matterNo ?? ''
      display = cn || mn ? `${cn}-${mn}` : ''
    }
    const el = combinedInputTextRef.current
    if (el && el.value !== String(display ?? '')) {
      el.value = String(display ?? '')
    }
  }, [storeFilters, filterType, isClientsTable, isKeyDatesTable, isExpensesTable, isOvertimeTable, isThirdPartyFormsTable, isDocumentBundlesTable])

  // Create a local ref to hold the input element.
  const localInputTextRef = useRef<HTMLInputElement>(null)
  // Use the external ref if provided; otherwise use our own.
  const combinedInputTextRef = externalInputRef || localInputTextRef

  // On submit, we read the value from the input via the ref and then update the store.
  const handleSubmit = () => {
    const enteredValue = combinedInputTextRef.current?.value ?? ''

    // Normalize blank inputs to null so that a change from null to '' is not interpreted as a change.
    const normalizedValue = enteredValue.trim() === '' ? null : enteredValue

    if (isExpensesTable) {
      setExpensesTableFilters({ [filterType]: normalizedValue })
    } else if (isOvertimeTable) {
      setOvertimeTableFilters({ [filterType]: normalizedValue })
    } else if (isThirdPartyFormsTable) {
      setThirdPartyFormsTableFilters({ [filterType]: normalizedValue })
    } else if (isDocumentBundlesTable) {
      setDocumentBundlesTableFilters({ filterType, value: normalizedValue })
    } else if (isClientsTable) {
      if (filterType === 'clientNo') {
        let clientNo = normalizedValue
        setClientsTableFilters({ filterType: 'clientNo', value: clientNo })
      } else {
        setClientsTableFilters({ filterType: filterType, value: normalizedValue })
      }
    } else {
      if (filterType === 'clientNo') {
        let clientNo = normalizedValue
        let matterNo = null

        if (normalizedValue && normalizedValue.includes('-')) {
          const parts = normalizedValue.split('-')
          matterNo = parts.pop() || null
          clientNo = parts.join('-')
        }
        if (isKeyDatesTable) {
          setKeyDatesTableFilters({ filterType: 'clientNo', value: clientNo, isMatterSpecific })
          setKeyDatesTableFilters({ filterType: 'matterNo', value: matterNo, isMatterSpecific })
        } else {
          setMattersTableFilters({ filterType: 'clientNo', value: clientNo })
          setMattersTableFilters({ filterType: 'matterNo', value: matterNo })
        }
      } else {
        if (isKeyDatesTable) {
          setKeyDatesTableFilters({ filterType, value: normalizedValue, isMatterSpecific })
        } else {
          setMattersTableFilters({ filterType: filterType, value: normalizedValue })
        }
      }
    }
  }

  // On clear, reset the input and update the store.
  const handleClear = () => {
    if (combinedInputTextRef.current) {
      combinedInputTextRef.current.value = ''
    }

    if (isExpensesTable) {
      setExpensesTableFilters({ [filterType]: null })
    } else if (isOvertimeTable) {
      setOvertimeTableFilters({ [filterType]: null })
    } else if (isThirdPartyFormsTable) {
      setThirdPartyFormsTableFilters({ [filterType]: null })
    } else if (isDocumentBundlesTable) {
      setDocumentBundlesTableFilters({ filterType, value: null })
    } else if (isClientsTable) {
      setClientsTableFilters({ filterType: filterType, value: null })
    } else if (!isClientsTable && !isKeyDatesTable && filterType === 'clientNo') {
      setMattersTableFilters({ filterType: filterType, value: null })
      setMattersTableFilters({ filterType: 'matterNo', value: null })
    } else if (isKeyDatesTable && filterType === 'clientNo') {
      setKeyDatesTableFilters({ filterType, value: null, isMatterSpecific })
      setKeyDatesTableFilters({ filterType: 'matterNo', value: null, isMatterSpecific })
    } else if (isKeyDatesTable && filterType !== 'clientNo') {
      setKeyDatesTableFilters({ filterType, value: null, isMatterSpecific })
    } else {
      setMattersTableFilters({ filterType: filterType, value: null })
    }
  }

  // Memoize the rendered JSX to avoid re-creating it on every parent re-render.
  const memoizedJSX = useMemo(() => {
    return (
      <MUIBox sx={{ display: 'flex', alignItems: 'center', position: 'relative', height: '32px', width: '100%' }}>
        <MUITextField
          defaultValue={initialTextValue}
          inputRef={combinedInputTextRef}
          placeholder={placeholder}
          type="text"
          variant="outlined"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSubmit()
            }
          }}
          sx={{
            height: '32px',
            padding: 0,
            width: '100%',
            [`& .MuiInputBase-input`]: {
              height: '32px',
              pt: 0,
              pb: 0,
              pr: 0,
              pl: '6px',
              width: '100%'
            }
          }}
          {...otherProps}
        />
        <MUIButton
          onClick={handleSubmit}
          sx={{
            cursor: 'pointer',
            position: 'absolute',
            right: '24px',
            top: '8px',
            height: '1rem',
            width: '1rem',
            padding: 0,
            minWidth: 0
          }}
        >
          <EnterIcon sx={{ height: '1rem', width: '1rem' }} />
        </MUIButton>
        <MUIButton
          onClick={handleClear}
          sx={{
            cursor: 'pointer',
            position: 'absolute',
            right: '8px',
            top: '12px',
            height: '0.6rem',
            width: '0.6rem',
            padding: 0,
            minWidth: 0
          }}
        >
          <CloseIcon sx={{ fill: 'black', height: '0.6rem', width: '0.6rem' }} />
        </MUIButton>
      </MUIBox>
    )
  }, [initialTextValue])

  return memoizedJSX
}

type StatusDataGridFilter = {
  props: GridRenderHeaderFilterProps
  isKeyDatesTable?: boolean
  isMatterSpecific?: boolean
}
/**
 * The Status filter manages several boolean states.
 * It now reads from and updates the mattersTableFilters in our store.
 */
export const StatusDataGridFilter = ({ props, isKeyDatesTable, isMatterSpecific }: StatusDataGridFilter) => {
  const {
    mattersTableFilters,
    setMattersTableFilters,
    mattersTableFilterCount,
    keyDatesTableFilters,
    matterSpecificKeyDatesTableFilters,
    setKeyDatesTableFilters,
    keyDatesTableFilterCount,
    matterSpecificKeyDatesTableFilterCount
  } = useDataGridFilterStore()
  const filters = isKeyDatesTable
    ? ((isMatterSpecific ? matterSpecificKeyDatesTableFilters.filters : keyDatesTableFilters.filters) ?? {})
    : (mattersTableFilters.filters ?? {})
  const [open, setOpen] = useState<boolean | null>(false)
  const [isArchived, setIsArchived] = useState<boolean | null>(false)
  const [isCompleted, setIsCompleted] = useState<boolean | null>(null)
  const [highRisk, setHighRisk] = useState<boolean | null>(null)
  const [isProspect, setIsProspect] = useState<boolean | null>(null)
  const [isWeekend, setIsWeekend] = useState<boolean | null>(null)
  const [isHoliday, setIsHoliday] = useState<boolean | null>(null)
  const [items, setItems] = useState<any[]>([])

  useEffect(() => {
    if (isKeyDatesTable) {
      const shouldResetDisplayedValue = isMatterSpecific
        ? matterSpecificKeyDatesTableFilterCount === 0
        : keyDatesTableFilterCount === 0

      if (shouldResetDisplayedValue) {
        setIsWeekend(false)
        setIsHoliday(false)
      }
    } else {
      const shouldResetDisplayedValue = mattersTableFilterCount === 0

      if (shouldResetDisplayedValue) {
        setOpen(false)
        setIsCompleted(false)
        setIsArchived(false)
        setHighRisk(false)
        setIsProspect(false)
      }
    }
  }, [
    isKeyDatesTable,
    keyDatesTableFilterCount,
    matterSpecificKeyDatesTableFilterCount,
    mattersTableFilterCount,
    isMatterSpecific
  ])

  useEffect(() => {
    if (isKeyDatesTable) {
      const { isWeekend: contextWeekend, isHoliday: contextHoliday } = filters
      if (contextWeekend !== null && contextWeekend !== undefined) {
        setIsWeekend(contextWeekend)
      }
      if (contextHoliday !== null && contextHoliday !== undefined) {
        setIsHoliday(contextHoliday)
      }
    } else {
      const {
        highRisk: contextHighRisk,
        isArchived: contextArchived,
        isCompleted: contextCompleted,
        isProspect: contextProspect
      } = filters
      if (contextHighRisk !== null && contextHighRisk !== undefined) {
        setHighRisk(contextHighRisk)
      }
      if (contextArchived !== null && contextArchived !== undefined) {
        setIsArchived(contextArchived)
      }
      if (contextCompleted !== null && contextCompleted !== undefined) {
        setIsCompleted(contextCompleted)
      }
      if (contextProspect !== null && contextProspect !== undefined) {
        setIsProspect(contextProspect)
      }
    }
  }, [filters])

  useEffect(() => {
    if (isKeyDatesTable) {
      setItems([
        {
          state: isWeekend,
          label: 'isWeekend',
          description: 'Weekend',
          pill: (
            <Pill
              label="Weekend"
              color="white"
              backgroundColor="warning.light"
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          )
        },
        {
          state: isHoliday,
          label: 'isHoliday',
          description: 'Bank holiday',
          pill: (
            <Pill
              label="Bank holiday"
              color="white"
              backgroundColor="warning.light"
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          )
        }
      ])
    } else {
      setItems([
        {
          state: open,
          label: 'open',
          description: 'Open',
          pill: (
            <Pill
              label="Open"
              color="white"
              backgroundColor="secondary.main"
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          )
        },
        {
          state: isArchived,
          label: 'isArchived',
          description: 'Archived',
          pill: (
            <Pill
              label="Archived"
              color="black"
              backgroundColor="background.mid"
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          )
        },
        {
          state: isCompleted,
          label: 'isCompleted',
          description: 'Completed',
          pill: (
            <Pill
              label="Completed"
              color="white"
              backgroundColor="success.main"
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          )
        },
        {
          state: highRisk,
          label: 'highRisk',
          description: 'High Risk',
          pill: (
            <MUITypography
              sx={{
                alignItems: 'center',
                backgroundColor: '#C33241',
                borderRadius: '1rem',
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                fontSize: '0.75rem',
                fontWeight: '600',
                height: '25px',
                justifyContent: 'center',
                mb: 0,
                textAlign: 'center',
                width: '94px',
                minWidth: '94px'
              }}
              variant="body2"
            >
              <AlertIcon
                sx={{
                  height: '1rem',
                  mr: '0.25rem',
                  width: '1rem',
                  [`& > path`]: {
                    fill: '#fff',
                    strokeWidth: '2px'
                  }
                }}
              />{' '}
              High Risk
            </MUITypography>
          )
        },
        {
          state: isProspect,
          label: 'isProspect',
          description: 'Prospect',
          pill: (
            <Pill
              label="Prospect"
              backgroundColor="support.sand"
              sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
            />
          )
        }
      ])
    }
  }, [isKeyDatesTable, isWeekend, isHoliday, open, isArchived, isCompleted, highRisk, isProspect])

  const handleChange = (filterType: string) => {
    if (isKeyDatesTable) {
      const prevState = { isWeekend, isHoliday }

      const filterActions: Record<
        string,
        () => {
          isWeekend: boolean | null
          isHoliday: boolean | null
        }
      > = {
        isWeekend: () => ({
          ...prevState,
          isWeekend: !prevState.isWeekend
        }),
        isHoliday: () => ({
          ...prevState,
          isHoliday: !prevState.isHoliday
        })
      }

      const newState = filterActions[filterType] ? filterActions[filterType]() : prevState

      setIsWeekend(newState.isWeekend)
      setIsHoliday(newState.isHoliday)

      setKeyDatesTableFilters({
        filterType: 'isWeekend',
        value: newState.isWeekend ? true : null,
        isMatterSpecific
      })

      setKeyDatesTableFilters({
        filterType: 'isHoliday',
        value: newState.isHoliday ? true : null,
        isMatterSpecific
      })
    } else {
      const prevState = { open, isCompleted, isArchived, highRisk, isProspect }

      const filterActions: Record<
        string,
        () => {
          open: boolean | null
          isCompleted: boolean | null
          isArchived: boolean | null
          highRisk: boolean | null
          isProspect: boolean | null
        }
      > = {
        open: () => {
          const toggledOpen = !prevState.open
          return {
            ...prevState,
            open: toggledOpen,
            isCompleted: toggledOpen ? false : prevState.isCompleted,
            isArchived: toggledOpen ? false : prevState.isArchived
          }
        },
        isCompleted: () => ({
          ...prevState,
          isCompleted: !prevState.isCompleted
        }),
        isArchived: () => ({
          ...prevState,
          isArchived: !prevState.isArchived
        }),
        highRisk: () => ({
          ...prevState,
          highRisk: !prevState.highRisk
        }),
        isProspect: () => ({
          ...prevState,
          isProspect: !prevState.isProspect
        })
      }

      const newState = filterActions[filterType] ? filterActions[filterType]() : prevState

      setOpen(newState.open)
      setIsCompleted(newState.isCompleted)
      setIsArchived(newState.isArchived)
      setHighRisk(newState.highRisk)
      setIsProspect(newState.isProspect)

      setMattersTableFilters({
        filterType: 'isCompleted',
        value: newState.open === true ? false : newState.isCompleted ? true : null
      })
      setMattersTableFilters({
        filterType: 'isArchived',
        value: newState.open === true ? false : newState.isArchived ? true : null
      })
      setMattersTableFilters({
        filterType: 'highRisk',
        value: newState.highRisk ? true : null
      })
      setMattersTableFilters({
        filterType: 'isProspect',
        value: newState.isProspect ? true : null
      })
    }
  }

  const handleClear = () => {
    if (isKeyDatesTable) {
      setIsWeekend(false)
      setIsHoliday(false)
      setKeyDatesTableFilters({
        filterType: 'isWeekend',
        value: null,
        isMatterSpecific
      })
      setKeyDatesTableFilters({
        filterType: 'isHoliday',
        value: null,
        isMatterSpecific
      })
      return
    } else {
      setOpen(false)
      setIsCompleted(false)
      setIsArchived(false)
      setHighRisk(false)
      setIsProspect(false)
      setMattersTableFilters({
        filterType: 'isCompleted',
        value: null
      })
      setMattersTableFilters({
        filterType: 'isArchived',
        value: null
      })
      setMattersTableFilters({
        filterType: 'highRisk',
        value: null
      })
      setMattersTableFilters({
        filterType: 'isProspect',
        value: null
      })
    }
  }

  const renderOptions = useCallback(
    (items: any) =>
      items.map((item: any) => {
        // Determine if this MenuItem should be disabled.
        // We only want to disable isArchived and isCompleted when open is selected.
        const shouldDisableWhenOpen = open === true && (item.label === 'isArchived' || item.label === 'isCompleted')
        const shouldDisableWhenArchived = item.label === 'open' && (isCompleted || isArchived)
        const shouldDisable = shouldDisableWhenOpen || shouldDisableWhenArchived

        return (
          <MUIMenuItem
            key={item.description}
            // Only trigger handleChange if the item is not disabled.
            onClick={() => {
              if (!shouldDisable) {
                handleChange(item.label)
              }
            }}
            // Use the disabled prop so that MUIMenuItem is properly rendered as disabled.
            // @ts-ignore
            disabled={shouldDisable}
            sx={{
              backgroundColor: item.state ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
              borderRadius: '4px',
              // Optionally change the cursor style for disabled items.
              cursor: shouldDisable ? 'not-allowed' : 'pointer',
              margin: '6px 6px',
              minHeight: '38px',
              minWidth: 'calc(264px - 38px)',
              pb: 0,
              pl: '8px',
              pt: 0,
              width: '100%'
            }}
          >
            {item.pill}
          </MUIMenuItem>
        )
      }),
    [handleChange, open]
  )

  const renderValue = () => {
    const selectedLabels = items.filter((item: any) => item.state === true).map((item: any) => item.description)
    if (selectedLabels.length === 1) return selectedLabels[0]
    if (selectedLabels.length > 1) return `${selectedLabels[0]} + ${selectedLabels.length - 1}`
    return 'Select'
  }

  return (
    <MUIBox sx={{ width: '100%' }}>
      <FormControlDataGridWrapper label="Select">
        <MUISelect
          // @ts-ignore

          data-testid="status-filter"
          multiple
          value={items.filter((i) => i.state === true).map((i) => i.label)}
          renderValue={renderValue}
          sx={{
            mb: 0,
            width: '100%',
            [`& .MuiSelect-select`]: {
              height: '18px !important',
              pt: '6px',
              pb: '6px',
              pl: '8px'
            },
            [`& .MuiSelect-nativeInput`]: {
              height: '32px !important',
              pt: 0,
              pb: 0,
              pl: '8px'
            },
            [`& .MuiSvgIcon-root`]: {
              right: 0
            }
          }}
        >
          {renderOptions(items)}
        </MUISelect>
        <MUIButton
          onClick={handleClear}
          sx={{
            cursor: 'pointer',
            position: 'absolute',
            right: '24px',
            top: '12px',
            height: '0.6rem',
            width: '0.6rem',
            padding: 0,
            minWidth: 0
          }}
        >
          <CloseIcon
            sx={{
              fill: 'black',
              height: '0.6rem',
              width: '0.6rem'
            }}
          />
        </MUIButton>
      </FormControlDataGridWrapper>
    </MUIBox>
  )
}

/**
 * A filter component for fee earners.
 * Updates the mattersTableFilters using our store.
 */
export const FeeEarnerDataGridFilter = ({
  filterType,
  isKeyDatesTable = false,
  isMatterSpecific = false
}: {
  filterType: string
  isKeyDatesTable?: boolean
  isMatterSpecific?: boolean
}) => {
  const {
    mattersTableFilters,
    setMattersTableFilters,
    mattersTableFilterCount,
    keyDatesTableFilters,
    matterSpecificKeyDatesTableFilters,
    setKeyDatesTableFilters,
    keyDatesTableFilterCount,
    matterSpecificKeyDatesTableFilterCount
  } = useDataGridFilterStore()
  const { feeEarners } = useFeeEarnerStore()
  const filters =
    (isKeyDatesTable
      ? isMatterSpecific
        ? matterSpecificKeyDatesTableFilters?.filters
        : keyDatesTableFilters?.filters
      : mattersTableFilters?.filters) || {}
  const [selectedFeeEarners, setSelectedFeeEarners] = useState<string[]>([])

  useEffect(() => {
    const storedIds = (filters[filterType] as string[] | null) ?? null
    if (!storedIds || !storedIds.length) {
      setSelectedFeeEarners([])
      return
    }

    if (!feeEarners || !feeEarners.length) {
      return
    }

    const idToLabel = new Map(feeEarners.map((fe: any) => [fe.feeEarnerId, fe.description]))
    const descriptions = storedIds.map((id) => idToLabel.get(id)).filter(Boolean) as string[]

    setSelectedFeeEarners(descriptions)
  }, [feeEarners, filters, filterType])

  useEffect(() => {
    const shouldResetDisplayedValue = isKeyDatesTable
      ? isMatterSpecific
        ? matterSpecificKeyDatesTableFilterCount === 0
        : keyDatesTableFilterCount === 0
      : mattersTableFilterCount === 0

    if (shouldResetDisplayedValue) setSelectedFeeEarners([])
  }, [
    isKeyDatesTable,
    isMatterSpecific,
    mattersTableFilterCount,
    keyDatesTableFilterCount,
    matterSpecificKeyDatesTableFilterCount
  ])

  if (!feeEarners) return <div data-testid="FeeEarnerFilter" />

  const handleChange = (event: any) => {
    const selectedDescriptions = event.target.value as string[]
    setSelectedFeeEarners(selectedDescriptions)
    const selectedFeeEarnerIds = feeEarners
      .filter((feeEarner: any) => selectedDescriptions.includes(feeEarner.description))
      .map((feeEarner: any) => feeEarner.feeEarnerId)
    if (isKeyDatesTable) {
      setKeyDatesTableFilters({
        filterType,
        value: selectedFeeEarnerIds.length ? selectedFeeEarnerIds : null,
        isMatterSpecific
      })
    } else {
      setMattersTableFilters({
        filterType,
        value: selectedFeeEarnerIds.length ? selectedFeeEarnerIds : null
      })
    }
  }

  const handleClear = () => {
    setSelectedFeeEarners([])
    if (isKeyDatesTable) {
      setKeyDatesTableFilters({ filterType, value: null, isMatterSpecific })
    } else {
      setMattersTableFilters({ filterType, value: null })
    }
  }

  return (
    <>
      <SelectDataGridFilterWrapper
        label="Select"
        value={selectedFeeEarners}
        onChange={handleChange}
        renderValue={defaultRenderValue}
      >
        {feeEarners.map((item: any) => (
          <MUIMenuItem key={`${item.description}-${uuidv4()}`} value={item.description} sx={menuItemStyles}>
            <Avatar
              initials={item.initials}
              feeEarnerId={item.feeEarnerId}
              name={item.description}
              sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }}
            />
            {item.description}
          </MUIMenuItem>
        ))}
      </SelectDataGridFilterWrapper>
      <MUIButton
        onClick={handleClear}
        sx={{
          cursor: 'pointer',
          position: 'absolute',
          right: '32px',
          top: '24px',
          height: '0.6rem',
          width: '0.6rem',
          padding: 0,
          minWidth: 0
        }}
      >
        <CloseIcon
          sx={{
            fill: 'black',
            height: '0.6rem',
            width: '0.6rem'
          }}
        />
      </MUIButton>
    </>
  )
}

/**
 * Key date type filter (multi-select) with stable option caching.
 * Options are sourced from current rows, but cached so they don't disappear after filtering.
 */
export const KeyDateTypeDataGridFilter = ({
  filterType,
  isMatterSpecific
}: {
  filterType: string
  isMatterSpecific?: boolean
}) => {
  const {
    keyDatesTableFilters,
    matterSpecificKeyDatesTableFilters,
    setKeyDatesTableFilters,
    keyDatesTableFilterCount,
    matterSpecificKeyDatesTableFilterCount
  } = useDataGridFilterStore()
  const { keyDatesTableData } = useKeyDateStore()

  const filters = (isMatterSpecific ? matterSpecificKeyDatesTableFilters?.filters : keyDatesTableFilters?.filters) || {}

  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [options, setOptions] = useState<Array<{ id: string; label: string }>>([])
  const cacheRef = useRef<Map<string, string>>(new Map()) // id -> label

  // Hydrate local selection from store
  useEffect(() => {
    const stored = (filters[filterType] as string[] | null) ?? null
    setSelectedIds(stored && stored.length ? stored.map(String) : [])
  }, [filters, filterType])

  // Keep options list stable: merge newly seen values into a cache
  useEffect(() => {
    const rows = keyDatesTableData?.rows || []
    for (const r of rows) {
      const label = (r?.keyDateTypeDescription ?? '').toString().trim()
      if (!label) continue
      const idRaw = r?.keyDateTypeId ?? r?.keyDateTypeRefId ?? r?.keyDateTypeIds?.[0] ?? label // fallback for unknown shapes
      const id = String(idRaw)
      cacheRef.current.set(id, label)
    }
    // Ensure currently selected IDs (from prefs) are present in cache (with best-effort labels)
    for (const id of selectedIds) {
      if (!cacheRef.current.has(id)) cacheRef.current.set(id, id)
    }
    // @ts-ignore
    const arr = Array.from(cacheRef.current.entries())
      .map(([id, label]: any) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label))
    setOptions(arr)
  }, [keyDatesTableData?.rows, selectedIds])

  useEffect(() => {
    if (!selectedIds.length) return
    // If any "id" is actually a label, convert it to a known id.
    const labelToId = new Map(options.map((o) => [o.label, o.id]))
    const normalized = selectedIds.map((v) => {
      if (cacheRef.current.has(v)) return v // already an ID
      return labelToId.get(v) ?? v
    })
    if (JSON.stringify(normalized) !== JSON.stringify(selectedIds)) {
      setSelectedIds(normalized)
      setKeyDatesTableFilters({ filterType, value: normalized, isMatterSpecific })
    }
  }, [options, selectedIds, filterType, isMatterSpecific, setKeyDatesTableFilters])

  // When all filters are cleared, keep the cache (so the user can re-open and multi-select),
  // but reset the *selection*
  useEffect(() => {
    const count = isMatterSpecific ? matterSpecificKeyDatesTableFilterCount : keyDatesTableFilterCount
    if (count === 0) setSelectedIds([])
  }, [isMatterSpecific, keyDatesTableFilterCount, matterSpecificKeyDatesTableFilterCount])

  const handleChange = (event: any) => {
    const ids = (event.target.value as any[]).map(String)
    setSelectedIds(ids)
    setKeyDatesTableFilters({ filterType, value: ids.length ? ids : null, isMatterSpecific })
  }

  const handleClear = () => {
    setSelectedIds([])
    setKeyDatesTableFilters({
      filterType,
      value: null,
      isMatterSpecific
    })
  }

  const renderSelected = (ids: string[]) => {
    if (!ids?.length) return 'Select'
    const labels = ids.map((id) => cacheRef.current.get(id) ?? id).filter(Boolean) as string[]
    if (labels.length === 1) return labels[0]
    return `${labels[0]} + ${labels.length - 1}`
  }

  return (
    <>
      <SelectDataGridFilterWrapper
        label="Select"
        value={selectedIds}
        onChange={handleChange}
        renderValue={renderSelected}
        inputProps={{ 'data-testid': 'key-date-type-filter' }}
      >
        {options.map((opt) => (
          <MUIMenuItem key={opt.id} value={opt.id} sx={menuItemStyles}>
            <Pill label={opt.label} color="black" backgroundColor="background.mid" />
          </MUIMenuItem>
        ))}
      </SelectDataGridFilterWrapper>

      <MUIButton
        onClick={handleClear}
        sx={{
          cursor: 'pointer',
          position: 'absolute',
          right: '32px',
          top: '24px',
          height: '0.6rem',
          width: '0.6rem',
          padding: 0,
          minWidth: 0
        }}
      >
        <CloseIcon sx={{ fill: 'black', height: '0.6rem', width: '0.6rem' }} />
      </MUIButton>
    </>
  )
}

/**
 * A filter component for work types.
 * Updates the mattersTableFilters using our store.
 */
export const WorkTypeDataGridFilter = ({ filterType }: { filterType: string }) => {
  const { mattersTableFilters, setMattersTableFilters, mattersTableFilterCount } = useDataGridFilterStore()
  const filters = mattersTableFilters.filters || {}
  const [selectedValues, setSelectedValues] = useState<string[]>([])
  const [options, setOptions] = useState<any[]>([])

  const { fetchWorkflowTypes, workflowTypes } = useLookupsStore()

  useEffect(() => {
    if (workflowTypes) {
      fetchWorkflowTypes()
      setOptions(workflowTypes)
    }
  }, [workflowTypes, fetchWorkflowTypes])

  useEffect(() => {
    const shouldResetDisplayedValue = mattersTableFilterCount === 0

    if (shouldResetDisplayedValue) setSelectedValues([])
  }, [mattersTableFilterCount])

  useEffect(() => {
    if (options && filters[filterType] !== null) {
      const storedIds = filters[filterType] as string[]
      const descriptions = options
        .filter((option: any) => storedIds.includes(option.workflowTypeId))
        .map((option: any) => option.description)
      setSelectedValues(descriptions)
    }
  }, [options, filters, filterType])

  const handleChange = (event: any) => {
    const value = event.target.value as string[]
    const selectedOptions = options.filter((option: any) => value.includes(option.description))
    const ids = selectedOptions.map((option: any) => option.workflowTypeId)
    setSelectedValues(ids)
    setMattersTableFilters({
      filterType: filterType,
      value: ids
    })
  }

  const handleClear = () => {
    setSelectedValues([])
    setMattersTableFilters({
      filterType: filterType,
      value: null
    })
  }

  return (
    <>
      <SelectDataGridFilterWrapper
        label="Select"
        value={selectedValues}
        onChange={handleChange}
        renderValue={defaultRenderValue}
      >
        {options.map((item: any) => (
          <MUIMenuItem key={`${item.description}-${uuidv4()}`} value={item.description} sx={menuItemStyles}>
            <Pill label={item.description} color="black" backgroundColor="background.mid" />
          </MUIMenuItem>
        ))}
      </SelectDataGridFilterWrapper>
      <MUIButton
        onClick={handleClear}
        sx={{
          cursor: 'pointer',
          position: 'absolute',
          right: '32px',
          top: '24px',
          height: '0.6rem',
          width: '0.6rem',
          padding: 0,
          minWidth: 0
        }}
      >
        <CloseIcon
          sx={{
            fill: 'black',
            height: '0.6rem',
            width: '0.6rem'
          }}
        />
      </MUIButton>
    </>
  )
}

type SingleSelectDataGridFilterProps = {
  filterType: string
  label: string
  /** Array of distinct raw option values (e.g. ['P', 'L'] or unique strings for branch names) */
  options: string[]
  /**
   * Optional mapping object to translate raw values to a user-friendly label.
   * For example: { P: 'Private', L: 'Legal Aid' }
   */
  valueMapping?: Record<string, string>
  defaultStatus?: string
  isClientsTable?: boolean
  isKeyDatesTable?: boolean
  isExpensesTable?: boolean
  isOvertimeTable?: boolean
  isMatterSpecific?: boolean
}

/**
 * A reusable single-select filter.
 * Updates the appropriate filters in our store.
 */
export const SingleSelectDataGridFilter = ({
  filterType,
  label,
  options,
  valueMapping,
  defaultStatus = '',
  isClientsTable = false,
  isKeyDatesTable = false,
  isExpensesTable = false,
  isOvertimeTable = false,
  isMatterSpecific = false
}: SingleSelectDataGridFilterProps) => {
  const {
    clientsTableFilterCount,
    clientsTableFilters,
    keyDatesTableFilterCount,
    keyDatesTableFilters,
    mattersTableFilterCount,
    mattersTableFilters,
    matterSpecificKeyDatesTableFilters,
    matterSpecificKeyDatesTableFilterCount,
    expensesTableFilters,
    expensesTableFilterCount,
    overtimeTableFilters,
    overtimeTableFilterCount,
    setClientsTableFilters,
    setKeyDatesTableFilters,
    setMattersTableFilters,
    setExpensesTableFilters,
    setOvertimeTableFilters
  } = useDataGridFilterStore()
  const [selectedValue, setSelectedValue] = useState<string | null>(null)

  useEffect(() => {
    const storeVal = isExpensesTable
      ? expensesTableFilters?.filters?.[filterType]
      : isOvertimeTable
        ? overtimeTableFilters?.filters?.[filterType]
        : isClientsTable
          ? clientsTableFilters?.filters?.[filterType]
          : isKeyDatesTable
            ? isMatterSpecific
              ? matterSpecificKeyDatesTableFilters?.filters?.[filterType]
              : keyDatesTableFilters?.filters?.[filterType]
            : mattersTableFilters?.filters?.[filterType]

    // only update local state if different to avoid unnecessary renders
    setSelectedValue((prev) => (prev !== (storeVal ?? null) ? (storeVal ?? null) : prev))
  }, [
    isClientsTable,
    isKeyDatesTable,
    isExpensesTable,
    isOvertimeTable,
    isMatterSpecific,
    filterType,
    clientsTableFilters?.filters,
    mattersTableFilters?.filters,
    keyDatesTableFilters?.filters,
    matterSpecificKeyDatesTableFilters?.filters,
    expensesTableFilters?.filters,
    overtimeTableFilters?.filters
  ])

  useEffect(() => {
    const shouldResetDisplayedValue = isExpensesTable
      ? expensesTableFilterCount === 0
      : isOvertimeTable
        ? overtimeTableFilterCount === 0
        : isClientsTable && !isKeyDatesTable
          ? clientsTableFilterCount === 0
          : isKeyDatesTable && !isClientsTable
            ? isMatterSpecific
              ? matterSpecificKeyDatesTableFilterCount === 0
              : keyDatesTableFilterCount === 0
            : mattersTableFilterCount === 0

    if (shouldResetDisplayedValue) setSelectedValue(null)
  }, [
    mattersTableFilterCount,
    clientsTableFilterCount,
    keyDatesTableFilterCount,
    expensesTableFilterCount,
    overtimeTableFilterCount,
    isClientsTable,
    isKeyDatesTable,
    isExpensesTable,
    isOvertimeTable
  ])

  const handleChange = (event: any) => {
    const value = event.target.value
    setSelectedValue(value)
    if (isExpensesTable) {
      setExpensesTableFilters({ [filterType]: value !== '' ? value : null })
    } else if (isOvertimeTable) {
      setOvertimeTableFilters({ [filterType]: value !== '' ? value : null })
    } else if (isClientsTable) {
      setClientsTableFilters({
        filterType: filterType,
        value: value !== '' ? value : null
      })
    } else if (isKeyDatesTable) {
      setKeyDatesTableFilters({
        filterType: filterType,
        value: value !== '' ? value : null,
        isMatterSpecific
      })
    } else {
      setMattersTableFilters({
        filterType: filterType,
        value: value !== '' ? value : null
      })
    }
  }

  const handleToggleClear = (opt: string) => {
    if (selectedValue === opt) {
      setSelectedValue(null)
      if (isExpensesTable) {
        setExpensesTableFilters({ [filterType]: null })
      } else if (isOvertimeTable) {
        setOvertimeTableFilters({ [filterType]: null })
      } else if (isClientsTable) {
        setClientsTableFilters({
          filterType: filterType,
          value: null
        })
      } else if (isKeyDatesTable) {
        setKeyDatesTableFilters({
          filterType: filterType,
          value: null,
          isMatterSpecific
        })
      } else {
        setMattersTableFilters({
          filterType: filterType,
          value: null
        })
      }
    }
  }

  const handleClear = () => {
    setSelectedValue(null)
    if (isExpensesTable) {
      setExpensesTableFilters({ [filterType]: null })
    } else if (isOvertimeTable) {
      setOvertimeTableFilters({ [filterType]: null })
    } else if (isClientsTable) {
      setClientsTableFilters({
        filterType: filterType,
        value: null
      })
    } else if (isKeyDatesTable) {
      setKeyDatesTableFilters({
        filterType: filterType,
        value: null,
        isMatterSpecific
      })
    } else {
      setMattersTableFilters({
        filterType: filterType,
        value: null
      })
    }
  }

  return (
    <FormControlDataGridWrapper label={label}>
      <MUISelect
        // @ts-ignore

        value={selectedValue ?? ''}
        onChange={handleChange}
        displayEmpty
        renderValue={(selected) => {
          if (selected === '' || selected === null) return defaultStatus
          return valueMapping ? (valueMapping[selected] ?? selected) : selected
        }}
        sx={{
          height: '32px',
          mb: 0,
          width: '100%',
          [`& .MuiFormLabel-root`]: {
            opacity: 0.6
          },
          [`& .MuiSelect-select`]: {
            height: '21px !important',
            pt: '6px',
            pb: '6px',
            pl: '8px',
            [`& .MuiSvgIcon-root`]: {
              position: 'relative',
              left: '1.5rem',
              top: '4px'
            }
          },
          [`& .MuiSelect-nativeInput`]: {
            height: '32px !important',
            pt: 0,
            pb: 0,
            pl: '8px'
          },
          [`& .MuiSvgIcon-root`]: {
            right: 0
          }
        }}
      >
        {options.map((opt) => {
          const displayLabel = valueMapping ? valueMapping[opt] || opt : opt
          return (
            <MUIMenuItem key={opt} value={opt} sx={menuItemStyles} onClick={() => handleToggleClear(opt)}>
              {displayLabel}
            </MUIMenuItem>
          )
        })}
      </MUISelect>
      <MUIButton
        onClick={handleClear}
        sx={{
          cursor: 'pointer',
          position: 'absolute',
          right: '24px',
          top: '12px',
          height: '0.6rem',
          width: '0.6rem',
          padding: 0,
          minWidth: 0
        }}
      >
        <CloseIcon
          sx={{
            fill: 'black',
            height: '0.6rem',
            width: '0.6rem'
          }}
        />
      </MUIButton>
    </FormControlDataGridWrapper>
  )
}

/**
 * A filter component for client portal status.
 * Uses the mattersTableFilters in our store.
 */
export const ClientPortalDataGridFilter = (props: GridRenderHeaderFilterProps) => {
  const { mattersTableFilters, setMattersTableFilters, mattersTableFilterCount } = useDataGridFilterStore()
  const filters = mattersTableFilters.filters || {}
  const [selectedValue, setSelectedValue] = useState<string>('')

  useEffect(() => {
    const shouldResetDisplayedValue = mattersTableFilterCount === 0

    if (shouldResetDisplayedValue) setSelectedValue('')
  }, [mattersTableFilterCount])

  const options = [
    {
      value: 'true',
      label: 'Enabled',
      pill: (
        <MUITypography
          sx={{
            backgroundColor: '#5073c3',
            borderRadius: '1rem',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '0.75rem',
            fontWeight: '700',
            height: '22px',
            marginTop: '8px',
            marginRight: '1rem',
            paddingTop: '1px',
            textAlign: 'center',
            transition: 'background-color 0.3s ease, color 0.3s ease, width 0.3s ease',
            width: '67px'
          }}
        >
          Enabled
        </MUITypography>
      )
    },
    {
      value: 'false',
      label: 'Not enabled',
      pill: (
        <MUITypography
          sx={{
            backgroundColor: 'rgba(195, 50, 65, 0.08)',
            borderRadius: '1rem',
            color: '#c33241',
            cursor: 'pointer',
            fontSize: '0.75rem',
            fontWeight: '700',
            height: '22px',
            marginTop: '8px',
            marginRight: '1rem',
            paddingTop: '1px',
            textAlign: 'center',
            transition: 'background-color 0.3s ease, color 0.3s ease, width 0.3s ease',
            width: '91px'
          }}
        >
          Not enabled
        </MUITypography>
      )
    }
  ]

  useEffect(() => {
    if (filters.isClientPortalEnabled !== null && filters.isClientPortalEnabled !== undefined) {
      setSelectedValue(String(filters.isClientPortalEnabled))
    }
  }, [filters])

  const handleChange = (e: any) => {
    const newValue = e.target.value as string
    const finalValue = newValue === selectedValue ? '' : newValue
    setSelectedValue(finalValue)
    const isEnabled = finalValue === 'true' ? true : finalValue === 'false' ? false : null
    setMattersTableFilters({
      filterType: 'isClientPortalEnabled',
      value: isEnabled
    })
  }

  const renderValue = (value: string) => {
    if (value === '') return ''
    return value === 'true' ? 'Enabled' : 'Not enabled'
  }

  return (
    <MUIBox sx={{ width: '100%' }}>
      <FormControlDataGridWrapper label="Select">
        <MUISelect
          // @ts-ignore

          data-testid="client-portal-status-filter"
          onChange={handleChange}
          value={selectedValue}
          renderValue={renderValue}
          sx={{
            mb: 0,
            width: '100%',
            [`& .MuiSelect-select`]: {
              height: '18px !important',
              pt: '6px',
              pb: '6px',
              pl: '8px'
            },
            [`& .MuiSelect-nativeInput`]: {
              height: '32px !important',
              pt: 0,
              pb: 0,
              pl: '8px'
            },
            [`& .MuiSvgIcon-root`]: {
              right: 0
            }
          }}
        >
          {options.map((option) => (
            <MUIMenuItem
              key={option.value}
              value={option.value}
              onClick={() => {
                if (selectedValue === option.value) {
                  setSelectedValue('')
                  setMattersTableFilters({
                    mattersTableFilters: {
                      ...mattersTableFilters,
                      filters: {
                        ...mattersTableFilters.filters,
                        isClientPortalEnabled: null
                      }
                    }
                  })
                }
              }}
            >
              {option.pill}
            </MUIMenuItem>
          ))}
        </MUISelect>
      </FormControlDataGridWrapper>
    </MUIBox>
  )
}

/**
 * A filter component for dates.
 * Updates the appropriate table filters in our store.
 */
export const DateDataGridFilter = ({
  filterType,
  isKeyDatesTable = false,
  isExpensesTable = false,
  isOvertimeTable = false,
  isThirdPartyFormsTable = false,
  isDocumentBundlesTable = false,
  isMatterSpecific
}: {
  filterType: string
  isKeyDatesTable?: boolean
  isExpensesTable?: boolean
  isOvertimeTable?: boolean
  isThirdPartyFormsTable?: boolean
  isDocumentBundlesTable?: boolean
  isMatterSpecific?: boolean
}) => {
  const {
    mattersTableFilters,
    setMattersTableFilters,
    mattersTableFilterCount,
    keyDatesTableFilters,
    setKeyDatesTableFilters,
    keyDatesTableFilterCount,
    matterSpecificKeyDatesTableFilters,
    matterSpecificKeyDatesTableFilterCount,
    expensesTableFilters,
    setExpensesTableFilters,
    expensesTableFilterCount,
    overtimeTableFilters,
    setOvertimeTableFilters,
    overtimeTableFilterCount,
    thirdPartyFormsTableFilters,
    setThirdPartyFormsTableFilters,
    thirdPartyFormsTableFilterCount,
    documentBundlesTableFilters,
    setDocumentBundlesTableFilters,
    documentBundlesTableFilterCount
  } = useDataGridFilterStore()
  const filters = isExpensesTable
    ? (expensesTableFilters?.filters ?? {})
    : isOvertimeTable
      ? (overtimeTableFilters?.filters ?? {})
      : isThirdPartyFormsTable
        ? (thirdPartyFormsTableFilters?.filters ?? {})
        : isDocumentBundlesTable
          ? (documentBundlesTableFilters?.filters ?? {})
          : isKeyDatesTable
            ? (isMatterSpecific ? matterSpecificKeyDatesTableFilters.filters : keyDatesTableFilters.filters) || {}
            : mattersTableFilters.filters || {}

  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [beforeAfterOpen, setBeforeAfterOpen] = useState<boolean>(false)
  const [comparisonOperator, setComparisonOperator] = useState<number>(0)
  const anchorRef = useRef<HTMLDivElement | null>(null)

  const hydratedRef = useRef(false)
  const skipNextPushRef = useRef(false)

  const coerceToDate = (v: any): Date | null => {
    if (!v) return null
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v
    if (typeof v === 'string') {
      const d = new Date(v)
      return isNaN(d.getTime()) ? null : d
    }
    // Dayjs or similar: prefer toDate(), fallback to .$d
    if (typeof v?.toDate === 'function') {
      try {
        const d = v.toDate()
        return d instanceof Date && !isNaN(d.getTime()) ? d : null
      } catch {}
    }
    if (v?.$d instanceof Date) {
      return isNaN(v.$d.getTime()) ? null : v.$d
    }
    return null
  }

  const toYmd = (d: Date): string => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  const normalizeToLocalDateOnly = (d: Date | string | null | undefined): Date | null => {
    if (!d) return null
    const dt = typeof d === 'string' ? new Date(d) : d
    if (Number.isNaN(dt.getTime())) return null
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate())
  }

  // hydrate from store
  useEffect(() => {
    const fv = filters[filterType] as { date: string | null; dateComparisonOperator?: number } | undefined
    if (!fv) return

    const nextOp = typeof fv.dateComparisonOperator === 'number' ? fv.dateComparisonOperator : 0
    const nextDate = normalizeToLocalDateOnly(fv.date)

    // only update state when it actually changed
    setComparisonOperator((prev) => (prev !== nextOp ? nextOp : prev))
    setSelectedDate((prev) => {
      const prevT = prev?.getTime() ?? null
      const nextT = nextDate?.getTime() ?? null
      return prevT !== nextT ? nextDate : prev
    })

    // this change came FROM the store; prevent the immediate push back
    skipNextPushRef.current = true
    if (!hydratedRef.current) hydratedRef.current = true
  }, [filters, filterType])

  useEffect(() => {
    if (!hydratedRef.current) return
    if (skipNextPushRef.current) {
      skipNextPushRef.current = false
      return
    }

    const payload = selectedDate
      ? { date: toYmd(selectedDate), dateComparisonOperator: comparisonOperator }
      : { date: null, dateComparisonOperator: comparisonOperator }

    const current = filters?.[filterType]
    if (JSON.stringify(current) !== JSON.stringify(payload)) {
      if (isExpensesTable) {
        setExpensesTableFilters({ [filterType]: payload })
      } else if (isOvertimeTable) {
        setOvertimeTableFilters({ [filterType]: payload })
      } else if (isThirdPartyFormsTable) {
        setThirdPartyFormsTableFilters({ [filterType]: payload })
      } else if (isDocumentBundlesTable) {
        setDocumentBundlesTableFilters({ filterType: filterType, value: payload })
      } else if (isKeyDatesTable) {
        setKeyDatesTableFilters({ filterType: filterType, value: payload })
      } else {
        setMattersTableFilters({ filterType, value: payload })
      }
    }
  }, [
    selectedDate,
    comparisonOperator,
    filters,
    filterType,
    isKeyDatesTable,
    isExpensesTable,
    isOvertimeTable,
    isThirdPartyFormsTable,
    isDocumentBundlesTable,
    setKeyDatesTableFilters,
    setMattersTableFilters,
    setExpensesTableFilters,
    setOvertimeTableFilters,
    setThirdPartyFormsTableFilters,
    setDocumentBundlesTableFilters
  ])

  // push only after hydrate
  useEffect(() => {
    const countZero = isExpensesTable
      ? expensesTableFilterCount === 0
      : isOvertimeTable
        ? overtimeTableFilterCount === 0
        : isThirdPartyFormsTable
          ? thirdPartyFormsTableFilterCount === 0
          : isDocumentBundlesTable
            ? documentBundlesTableFilterCount === 0
            : isKeyDatesTable
              ? isMatterSpecific
                ? matterSpecificKeyDatesTableFilterCount === 0
                : keyDatesTableFilterCount === 0
              : mattersTableFilterCount === 0
    const storeHasDate = Boolean((filters?.[filterType] as any)?.date)
    if (countZero && !storeHasDate) {
      setSelectedDate(null)
      setComparisonOperator(0)
      // prevent writing {date:null} redundantly
      skipNextPushRef.current = true
    }
  }, [isKeyDatesTable, isExpensesTable, isOvertimeTable, isThirdPartyFormsTable, isDocumentBundlesTable, keyDatesTableFilterCount, mattersTableFilterCount, expensesTableFilterCount, overtimeTableFilterCount, thirdPartyFormsTableFilterCount, documentBundlesTableFilterCount, filters, filterType])

  const handleDateChange = (incoming: any) => {
    const js = coerceToDate(incoming)
    const normalized = normalizeToLocalDateOnly(js)
    setSelectedDate(normalized)

    const payload = normalized
      ? { date: toYmd(normalized), dateComparisonOperator: comparisonOperator }
      : { date: null, dateComparisonOperator: comparisonOperator }

    // Push to the store immediately so a fetch is guaranteed to see it
    if (isExpensesTable) {
      setExpensesTableFilters({ [filterType]: payload })
    } else if (isOvertimeTable) {
      setOvertimeTableFilters({ [filterType]: payload })
    } else if (isThirdPartyFormsTable) {
      setThirdPartyFormsTableFilters({ [filterType]: payload })
    } else if (isDocumentBundlesTable) {
      setDocumentBundlesTableFilters({ filterType, value: payload })
      setDocumentBundlesTableFilters({ startAtIndex: 0 }) // reset pagination
    } else if (isKeyDatesTable) {
      setKeyDatesTableFilters({ filterType, value: payload, isMatterSpecific })
      setKeyDatesTableFilters({ startAtIndex: 0, isMatterSpecific }) // reset pagination per-scope
    } else {
      setMattersTableFilters({ filterType, value: payload })
    }
  }
  const handleBeforeAfterClick = (operator: number) => {
    setComparisonOperator(operator)
  }

  return (
    <MUIBox
      ref={anchorRef}
      sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}
      onKeyDown={(e) => e.stopPropagation()}
      onMouseDownCapture={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <FormControlDataGridWrapper
        label={
          comparisonOperator === 1 ? 'AFTER OR EQUAL TO' : comparisonOperator === 2 ? 'BEFORE OR EQUAL TO' : 'EQUAL TO'
        }
        sx={{
          border: '1px solid #b8b9bb',
          borderRadius: '4px',
          color: 'rgba(5, 28, 51, 0.6)',
          fontSize: '0.875rem',
          fontWeight: '400',
          padding: '5px 2px',
          [`& .MuiTypography-root`]: { fontWeight: '400' }
        }}
        shrink={true}
      >
        <DateSelection
          format="d MMM yyyy"
          value={selectedDate}
          onChange={handleDateChange} // <-- now robust + pushes immediately
          setBeforeAfterOpen={setBeforeAfterOpen}
          comparisonOperator={comparisonOperator}
          size="small"
          sx={{
            '.MuiOutlinedInput-root': { display: 'none' },
            '.MuiFormLabel-root': { display: 'none' },
            '.MuiTextField-root': { height: 0 }
          }}
          dataGridFilter
        />
        <MUIPopper open={beforeAfterOpen} anchorEl={anchorRef.current} placement="bottom-end">
          <MUIPaper
            sx={{
              alignItems: 'flex-start',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              gap: 2,
              padding: 2,
              textAlign: 'left'
            }}
          >
            <MUIButton
              onClick={() => {
                handleBeforeAfterClick(0)
                setBeforeAfterOpen(false)
              }}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', textAlign: 'left' }}
            >
              {`Equal to`}
            </MUIButton>
            <MUIButton
              onClick={() => {
                handleBeforeAfterClick(2)
                setBeforeAfterOpen(false)
              }}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', textAlign: 'left' }}
            >
              {`Before or equal to`}
            </MUIButton>
            <MUIButton
              onClick={() => {
                handleBeforeAfterClick(1)
                setBeforeAfterOpen(false)
              }}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', textAlign: 'left' }}
            >
              {`After or equal to`}
            </MUIButton>
          </MUIPaper>
        </MUIPopper>
      </FormControlDataGridWrapper>
    </MUIBox>
  )
}

/**
 * A filter component for balances/currency values.
 * Updates the appropriate table filters in our store.
 */
export const BalanceDataGridFilter = ({
  filterType,
  isExpensesTable = false,
  isOvertimeTable = false,
  inputRef: externalInputBalanceRef,
  ...otherProps
}: {
  filterType: string
  isExpensesTable?: boolean
  isOvertimeTable?: boolean
  inputRef?: React.RefObject<HTMLInputElement>
}) => {
  const {
    mattersTableFilters,
    setMattersTableFilters,
    mattersTableFilterCount,
    expensesTableFilters,
    setExpensesTableFilters,
    expensesTableFilterCount,
    overtimeTableFilters,
    setOvertimeTableFilters,
    overtimeTableFilterCount
  } = useDataGridFilterStore()

  useEffect(() => {
    const shouldResetDisplayedValue = isExpensesTable
      ? expensesTableFilterCount === 0
      : isOvertimeTable
        ? overtimeTableFilterCount === 0
        : mattersTableFilterCount === 0

    if (shouldResetDisplayedValue && combinedInputBalanceRef?.current) combinedInputBalanceRef.current.value = ''
  }, [mattersTableFilterCount, expensesTableFilterCount, overtimeTableFilterCount, isExpensesTable, isOvertimeTable])

  const filters = isExpensesTable
    ? (expensesTableFilters?.filters ?? {})
    : isOvertimeTable
      ? (overtimeTableFilters?.filters ?? {})
      : mattersTableFilters.filters || {}

  // Derive the initial value once from the store.
  const initialBalanceValue =
    filters[filterType] && filters[filterType].value !== undefined ? String(filters[filterType].value) : ''

  // Create a local ref to hold the input element.
  const localInputBalanceRef = useRef<HTMLInputElement>(null)
  // Use the external ref if provided; otherwise use our own.
  const combinedInputBalanceRef = externalInputBalanceRef || localInputBalanceRef

  // Retain the comparison operator in state (it is controlled).
  const [comparisonOperator, setComparisonOperator] = useState<number>(0)
  const [beforeAfterOpen, setBeforeAfterOpen] = useState<boolean>(false)
  const anchorRef = useRef<HTMLDivElement | null>(null)

  // If the store's filter for this field exists, initialize the comparison operator.
  // Also clear the input if the filter value is cleared in the store.
  useEffect(() => {
    const filterValue = filters[filterType]
    if (filterValue !== null && filterValue !== undefined) {
      if (filterValue.numberComparisonOperator !== undefined) {
        setComparisonOperator(filterValue.numberComparisonOperator)
      }
      // Clear the input if the store value is null/empty
      if (filterValue.value === null || filterValue.value === undefined || filterValue.value === '') {
        if (combinedInputBalanceRef.current) {
          combinedInputBalanceRef.current.value = ''
        }
        setComparisonOperator(0)
      }
    } else {
      // Filter was completely removed, clear the input
      if (combinedInputBalanceRef.current) {
        combinedInputBalanceRef.current.value = ''
      }
      setComparisonOperator(0)
    }
  }, [filters, filterType])

  // Submit: Read the current value from the ref (thus keeping the input uncontrolled)
  const handleSubmit = () => {
    if (
      combinedInputBalanceRef.current?.value === null ||
      combinedInputBalanceRef.current?.value === undefined ||
      combinedInputBalanceRef.current?.value === ''
    )
      return

    const payload = { value: combinedInputBalanceRef.current?.value, numberComparisonOperator: comparisonOperator }

    if (isExpensesTable) {
      setExpensesTableFilters({ [filterType]: payload })
    } else if (isOvertimeTable) {
      setOvertimeTableFilters({ [filterType]: payload })
    } else {
      setMattersTableFilters({
        filterType: filterType,
        value: payload
      })
    }
  }

  // Clear: Reset the input using the ref and update the store.
  const handleClear = () => {
    if (combinedInputBalanceRef.current) {
      combinedInputBalanceRef.current.value = ''
    }

    const payload = { value: null, numberComparisonOperator: null }

    if (isExpensesTable) {
      setExpensesTableFilters({ [filterType]: payload })
    } else if (isOvertimeTable) {
      setOvertimeTableFilters({ [filterType]: payload })
    } else {
      setMattersTableFilters({
        filterType: filterType,
        value: payload
      })
    }
  }

  // beforeAfterOpen handling remains unchanged:
  const handleBeforeAfterClick = (operator: number) => {
    setComparisonOperator(operator)

    // Also update the store if there's a value in the input
    const currentValue = combinedInputBalanceRef.current?.value
    if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
      const payload = { value: currentValue, numberComparisonOperator: operator }

      if (isExpensesTable) {
        setExpensesTableFilters({ [filterType]: payload })
      } else if (isOvertimeTable) {
        setOvertimeTableFilters({ [filterType]: payload })
      } else {
        setMattersTableFilters({
          filterType: filterType,
          value: payload
        })
      }
    }
  }

  // Memoize the JSX output.
  const memoizedJSX = useMemo(() => {
    return (
      <MUIBox
        ref={anchorRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          width: '100%',
          position: 'relative'
        }}
      >
        <FormControlDataGridWrapper
          label={
            comparisonOperator === 1
              ? 'GREATER THAN OR EQUAL TO'
              : comparisonOperator === 2
                ? 'LESS THAN OR EQUAL TO'
                : 'EQUAL TO'
          }
          shrink={true}
        >
          <MUITextField
            defaultValue={initialBalanceValue}
            inputRef={combinedInputBalanceRef}
            placeholder="Enter amount"
            type="number"
            variant="outlined"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSubmit()
              }
            }}
            sx={{
              height: '32px',
              padding: 0,
              width: '100%',
              [`& .MuiInputBase-input`]: {
                height: '32px',
                pt: 0,
                pb: 0,
                pr: 0,
                pl: '6px',
                width: '100%'
              }
            }}
            {...otherProps}
          />
        </FormControlDataGridWrapper>
        <MUIButton
          onClick={() => handleSubmit()}
          sx={{
            cursor: 'pointer',
            position: 'absolute',
            right: '48px',
            top: '8px',
            height: '1rem',
            width: '1rem',
            padding: 0,
            minWidth: 0
          }}
        >
          <EnterIcon sx={{ height: '1rem', width: '1rem' }} />
        </MUIButton>
        <MUIButton
          onClick={() => setBeforeAfterOpen((prev) => !prev)}
          sx={{
            width: '14px',
            minWidth: '14px',
            position: 'absolute',
            ml: '4px',
            padding: 0,
            right: '30px'
          }}
        >
          <FilterIcon sx={{ height: '14px', width: '14px' }} />
        </MUIButton>
        <MUIButton
          onClick={handleClear}
          sx={{
            cursor: 'pointer',
            position: 'absolute',
            right: '16px',
            top: '12px',
            height: '0.6rem',
            width: '0.6rem',
            padding: 0,
            minWidth: 0
          }}
        >
          <CloseIcon sx={{ fill: 'black', height: '0.6rem', width: '0.6rem' }} />
        </MUIButton>
        <MUIPopper open={beforeAfterOpen} anchorEl={anchorRef.current} placement="bottom-end">
          <MUIPaper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              gap: 2,
              padding: 2,
              textAlign: 'left'
            }}
          >
            <MUIButton
              onClick={() => {
                handleBeforeAfterClick(0)
                setBeforeAfterOpen(false)
              }}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', textAlign: 'left' }}
            >
              {`Equal to`}
            </MUIButton>
            <MUIButton
              onClick={() => {
                handleBeforeAfterClick(2)
                setBeforeAfterOpen(false)
              }}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', textAlign: 'left' }}
            >
              {`Less than or equal to`}
            </MUIButton>
            <MUIButton
              onClick={() => {
                handleBeforeAfterClick(1)
                setBeforeAfterOpen(false)
              }}
              sx={{ justifyContent: 'flex-start', textTransform: 'none', textAlign: 'left' }}
            >
              {`Greater than or equal to`}
            </MUIButton>
          </MUIPaper>
        </MUIPopper>
      </MUIBox>
    )
  }, [initialBalanceValue, comparisonOperator, beforeAfterOpen])

  return memoizedJSX
}

/**
 * Status items for expenses table
 */
const expensesStatusItems = [
  { value: 1, label: 'SUBMITTED', color: 'black', backgroundColor: '#DFE0E4' },
  { value: 2, label: 'APPROVED', color: 'white', backgroundColor: '#377F86' },
  { value: 3, label: 'REJECTED', color: 'white', backgroundColor: 'error.main' },
  { value: 4, label: 'ACTION REQUIRED', color: 'white', backgroundColor: '#DE6A1E' },
  { value: 5, label: 'READY TO PAY', color: 'white', backgroundColor: '#5073C3' },
  { value: 6, label: 'POSTED', color: 'white', backgroundColor: '#647495' },
  { value: 7, label: 'PAID', color: 'white', backgroundColor: '#051C33' }
]

const expensesSubmissionStatusItems = [
  { value: 0, label: 'DRAFT', color: 'black', backgroundColor: '#DFE0E4' },
  { value: 1, label: 'SUBMITTED', color: 'black', backgroundColor: '#DFE0E4' },
  { value: 2, label: 'APPROVED', color: 'white', backgroundColor: '#377F86' },
  { value: 3, label: 'REJECTED', color: 'white', backgroundColor: 'error.main' },
  { value: 4, label: 'ACTION REQUIRED', color: 'white', backgroundColor: '#DE6A1E' },
  { value: 5, label: 'READY TO PAY', color: 'white', backgroundColor: '#5073C3' },
  { value: 6, label: 'POSTED', color: 'white', backgroundColor: '#647495' },
  { value: 7, label: 'PAID', color: 'white', backgroundColor: '#051C33' }
]

/**
 * Status items for overtime table
 */
const overtimeStatusItems = [
  { value: 1, label: 'SUBMITTED', color: 'black', backgroundColor: '#DFE0E4' },
  { value: 2, label: 'APPROVED', color: 'white', backgroundColor: '#377F86' },
  { value: 3, label: 'REJECTED', color: 'white', backgroundColor: 'error.main' },
  { value: 4, label: 'ACTION REQUIRED', color: 'white', backgroundColor: '#DE6A1E' },
  { value: 5, label: 'READY TO PAY', color: 'white', backgroundColor: '#5073C3' },
  { value: 6, label: 'POSTED', color: 'white', backgroundColor: '#647495' },
  { value: 8, label: 'TOIL AGREED', color: 'white', backgroundColor: '#051C33' },
  { value: 7, label: 'PAID', color: 'white', backgroundColor: '#051C33' }
]

type ClaimsStatusFilterProps = {
  isExpensesTable?: boolean
  isOvertimeTable?: boolean
  isSubmissionVersion?: boolean
}

/**
 * A filter component for claims status (expenses and overtime tables).
 * Supports multi-select status filtering with visual pills.
 */
export const ClaimsStatusFilter = ({
  isExpensesTable = true,
  isOvertimeTable = false,
  isSubmissionVersion = false
}: ClaimsStatusFilterProps) => {
  const theme = useTheme()
  const {
    expensesTableFilters,
    setExpensesTableFilters,
    expensesTableFilterCount,
    overtimeTableFilters,
    setOvertimeTableFilters,
    overtimeTableFilterCount
  } = useDataGridFilterStore()

  const filters = isExpensesTable
    ? (expensesTableFilters?.filters ?? {})
    : (overtimeTableFilters?.filters ?? {})

  const filterCount = isExpensesTable ? expensesTableFilterCount : overtimeTableFilterCount

  const statusItems = isOvertimeTable
    ? overtimeStatusItems
    : isSubmissionVersion
      ? expensesSubmissionStatusItems
      : expensesStatusItems

  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([])

  useEffect(() => {
    const storedStatuses = filters?.status as number[] | null
    if (storedStatuses && Array.isArray(storedStatuses)) {
      setSelectedStatuses(storedStatuses)
    } else {
      setSelectedStatuses([])
    }
  }, [filters?.status])

  useEffect(() => {
    if (filterCount === 0) {
      setSelectedStatuses([])
    }
  }, [filterCount])

  const handleChange = (statusValue: number) => {
    const isSelected = selectedStatuses.includes(statusValue)
    let newStatuses: number[]

    if (isSelected) {
      newStatuses = selectedStatuses.filter((s) => s !== statusValue)
    } else {
      newStatuses = [...selectedStatuses, statusValue]
    }

    setSelectedStatuses(newStatuses)

    const storeValue = newStatuses.length > 0 ? newStatuses : null

    if (isExpensesTable) {
      setExpensesTableFilters({ status: storeValue })
    } else {
      setOvertimeTableFilters({ status: storeValue })
    }
  }

  const handleClear = () => {
    setSelectedStatuses([])
    if (isExpensesTable) {
      setExpensesTableFilters({ status: null })
    } else {
      setOvertimeTableFilters({ status: null })
    }
  }

  const renderValue = () => {
    if (selectedStatuses.length === 0) return 'Select'
    if (selectedStatuses.length === 1) {
      const item = statusItems.find((i) => i.value === selectedStatuses[0])
      return item?.label ?? String(selectedStatuses[0])
    }
    const firstItem = statusItems.find((i) => i.value === selectedStatuses[0])
    return `${firstItem?.label ?? String(selectedStatuses[0])} + ${selectedStatuses.length - 1}`
  }

  const getBackgroundColor = (item: typeof statusItems[0]) => {
    if (item.backgroundColor === 'error.main') {
      return theme.palette.error.main
    }
    return item.backgroundColor
  }

  return (
    <MUIBox sx={{ width: '100%' }}>
      <FormControlDataGridWrapper label="Select">
        <MUISelect
          data-testid="claims-status-filter"
          multiple
          value={selectedStatuses}
          renderValue={renderValue}
          sx={{
            mb: 0,
            width: '100%',
            [`& .MuiSelect-select`]: {
              height: '18px !important',
              pt: '6px',
              pb: '6px',
              pl: '8px'
            },
            [`& .MuiSelect-nativeInput`]: {
              height: '32px !important',
              pt: 0,
              pb: 0,
              pl: '8px'
            },
            [`& .MuiSvgIcon-root`]: {
              right: 0
            }
          }}
        >
          {statusItems.map((item) => (
            <MUIMenuItem
              key={item.value}
              value={item.value}
              onClick={() => handleChange(item.value)}
              sx={{
                backgroundColor: selectedStatuses.includes(item.value) ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                borderRadius: '4px',
                cursor: 'pointer',
                margin: '6px 6px',
                minHeight: '38px',
                minWidth: 'calc(264px - 38px)',
                pb: 0,
                pl: '8px',
                pt: 0,
                width: '100%'
              }}
            >
              <Pill
                label={item.label}
                color={item.color}
                backgroundColor={getBackgroundColor(item)}
                sx={{ height: '25px', [`& span`]: { fontSize: '0.75rem' } }}
              />
            </MUIMenuItem>
          ))}
        </MUISelect>
        <MUIButton
          onClick={handleClear}
          sx={{
            cursor: 'pointer',
            position: 'absolute',
            right: '24px',
            top: '12px',
            height: '0.6rem',
            width: '0.6rem',
            padding: 0,
            minWidth: 0
          }}
        >
          <CloseIcon
            sx={{
              fill: 'black',
              height: '0.6rem',
              width: '0.6rem'
            }}
          />
        </MUIButton>
      </FormControlDataGridWrapper>
    </MUIBox>
  )
}

type ClaimRecipientFilterProps = {
  filterType: string
  isExpensesTable?: boolean
  isOvertimeTable?: boolean
  isMatterSpecific?: boolean
  label?: string
  testId?: string
}

export const ClaimRecipientFilter = ({
  filterType,
  isExpensesTable = true,
  isOvertimeTable = false,
  label = 'Select',
  testId = 'claim-recipient-filter'
}: ClaimRecipientFilterProps) => {
  const {
    expensesTableFilters,
    setExpensesTableFilters,
    expensesTableFilterCount,
    overtimeTableFilters,
    setOvertimeTableFilters,
    overtimeTableFilterCount
  } = useDataGridFilterStore()

  const { fetchAllUsers, allUsers } = useLookupsStore()

  // Pull the right filter scope
  const filters = isExpensesTable
    ? (expensesTableFilters?.filters ?? {})
    : (overtimeTableFilters?.filters ?? {})

  const filterCount = isExpensesTable ? expensesTableFilterCount : overtimeTableFilterCount

  // Fetch options once (throttle in store protects you anyway)
  useEffect(() => {
    if (!allUsers || (Array.isArray(allUsers) && allUsers.length === 0)) {
      fetchAllUsers()
    }
  }, [allUsers, fetchAllUsers])

  // Be defensive about backend shapes
  const normalizeUsers = useMemo(() => {
    const arr = Array.isArray(allUsers) ? allUsers : []
    return arr
      .map((u: any) => {
        const id =
          u?.userId ??
          u?.bundleUserId ??
          u?.id ??
          u?.feeEarnerId ?? // just-in-case reuse
          null

        const name =
          u?.description ??
          u?.name ??
          u?.fullName ??
          u?.displayName ??
          u?.email ??
          String(id ?? '')

        const initials =
          u?.initials ??
          (typeof name === 'string'
            ? name
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((p) => p[0]?.toUpperCase())
                .join('')
            : '')

        if (!id) return null
        return { id: String(id), name: String(name), initials: String(initials || '') }
      })
      .filter(Boolean) as Array<{ id: string; name: string; initials: string }>
  }, [allUsers])

  const idToName = useMemo(() => new Map(normalizeUsers.map((u) => [u.id, u.name])), [normalizeUsers])

  const [selectedIds, setSelectedIds] = useState<string[]>([])

  // Hydrate selection from store
  useEffect(() => {
    const stored = filters?.[filterType] as string[] | null | undefined
    const next = Array.isArray(stored) ? stored.map(String) : []
    setSelectedIds((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next))
  }, [filters, filterType])

  // Reset UI when all filters cleared
  useEffect(() => {
    if (filterCount === 0) setSelectedIds([])
  }, [filterCount])

  const pushToStore = (ids: string[]) => {
    const value = ids.length ? ids : null
    if (isExpensesTable) {
      setExpensesTableFilters({ [filterType]: value })
    } else {
      setOvertimeTableFilters({ [filterType]: value })
    }
  }

  const handleChange = (event: any) => {
    const ids = (event.target.value as any[]).map(String)
    setSelectedIds(ids)
    pushToStore(ids)
  }

  const handleClear = () => {
    setSelectedIds([])
    pushToStore([])
  }

  const renderSelected = (ids: string[]) => {
    if (!ids?.length) return 'Select'
    const labels = ids.map((id) => idToName.get(id) ?? id).filter(Boolean) as string[]
    if (labels.length === 1) return labels[0]
    return `${labels[0]} + ${labels.length - 1}`
  }

  return (
    <>
      <SelectDataGridFilterWrapper
        label={label}
        value={selectedIds}
        onChange={handleChange}
        renderValue={renderSelected}
        inputProps={{ 'data-testid': testId }}
      >
        {normalizeUsers
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((u) => (
            <MUIMenuItem key={u.id} value={u.id} sx={menuItemStyles}>
              <Avatar
                initials={u.initials}
                // If Avatar expects feeEarnerId only, you can remove these props and just show initials/name
                feeEarnerId={u.id as any}
                name={u.name}
                sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }}
              />
              {u.name}
            </MUIMenuItem>
          ))}
      </SelectDataGridFilterWrapper>

      <MUIButton
        onClick={handleClear}
        sx={{
          cursor: 'pointer',
          position: 'absolute',
          right: '32px',
          top: '24px',
          height: '0.6rem',
          width: '0.6rem',
          padding: 0,
          minWidth: 0
        }}
      >
        <CloseIcon sx={{ fill: 'black', height: '0.6rem', width: '0.6rem' }} />
      </MUIButton>
    </>
  )
}
